import { create } from 'zustand';
import type { Chess } from '@chess/api-chess';
import { gameMetrics, type GameMetrics } from './text';
import type { GameStore, GameSummary, Phase } from '@chess/api-model';
import { decidingStage } from '@chess/api-verdict';

// The selected game (`?game=`, the newest of the last 30 days without one)
// and what the card says about it: this plugin's own state, read from the
// game store and kept current by its subscription, so an import makes the
// card appear or change on its own.
// The metrics (each stage's score, blunders and misses) are the game's facts
// once its scan is complete; the store already holds them.

export type Status = 'loading' | 'ready' | 'error';

export interface Latest {
  game: GameSummary;
  /** The position the game ended on. */
  finalFen: string;
  plies: number;
  headers: Record<string, string>;
  /** The engine's verdict; undefined until the game's scan is complete. */
  metrics?: GameMetrics;
  /** The reader's average accuracy over the period's scanned games: the card's "your usual" (the verdict card's, until 2026-10-10). */
  usualAccuracy?: number;
  /** The stage that decided the game, by the verdict's rule (`decidingStage`): its score marked on the card. */
  decided?: Phase;
}

interface Deps {
  store: GameStore;
  chess: Chess;
  /** The game to show; the newest when undefined or no longer held. */
  id?: string;
}

interface State {
  status: Status;
  latest?: Latest;
  error?: string;
  load(deps: Deps): Promise<void>;
}

export const useLatest = create<State>((set) => ({
  status: 'loading',
  async load({ store, chess, id }) {
    try {
      await store.ready();
      const [summary] = (id && store.game(id) ? [store.game(id)!.summary] : undefined) ?? store.list({ order: 'newest', size: 1 });
      const record = summary && (await store.record(summary.id));
      if (!summary || !record) {
        set({ status: 'ready', latest: undefined, error: undefined });
        return;
      }
      let finalFen = chess.startFen;
      let plies = 0;
      let headers: Record<string, string> = {};
      try {
        const parsed = chess.parsePgn(record.pgn);
        finalFen = parsed.positions[parsed.positions.length - 1] ?? chess.startFen;
        plies = parsed.moves.length;
        headers = parsed.headers;
      } catch {
        // An unreadable PGN still leaves a game to show: the names, the result, the date.
      }
      const games = store.getState().games;
      const facts = games.find((g) => g.summary.id === summary.id)?.facts;
      const metrics = gameMetrics(facts);
      const accuracies = games.map((g) => g.facts?.accuracy).filter((a): a is number => a !== undefined);
      const usualAccuracy = accuracies.length ? accuracies.reduce((n, a) => n + a, 0) / accuracies.length : undefined;
      const deciding = facts ? decidingStage(facts) : undefined;
      const decided = deciding?.kind === 'stage' ? deciding.stage : undefined;
      set({ status: 'ready', latest: { game: summary, finalFen, plies, headers, metrics, usualAccuracy, decided }, error: undefined });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  },
}));

/**
 * Loads the game (`deps.id`, the newest without one) and reloads it whenever
 * that game changes or its scan completes, until unsubscribed.
 */
export function follow(deps: Deps): () => void {
  const reload = () => void useLatest.getState().load(deps);
  // What the card shows depends on the game shown and whether it is scanned.
  const keyOf = () => {
    const games = deps.store.getState().games;
    const shown = games.find((g) => g.summary.id === deps.id) ?? games[0];
    return shown ? `${shown.summary.id}|${shown.facts?.scanned ?? ''}|${shown.summary.result}` : '';
  };
  let shown = keyOf();
  reload();
  return deps.store.subscribe(() => {
    const key = keyOf();
    if (key === shown) return;
    shown = key;
    reload();
  });
}
